import { useState } from "react";

/** B.9: Webhook Management UI */

interface WebhookEndpoint {
  id: string;
  url: string;
  status: "active" | "disabled" | "failing";
  events: string[];
  successRate: number;
  lastDelivery: string | null;
  createdAt: string;
}

const AVAILABLE_EVENTS = [
  "contact.created", "contact.updated", "contact.deleted",
  "deal.created", "deal.updated", "deal.stage_changed", "deal.won", "deal.lost",
  "booking.created", "booking.completed", "booking.cancelled",
  "payment.completed", "payment.failed",
  "form.submitted",
  "email.sent", "email.bounced",
];

export default function WebhooksPage() {
  const [endpoints] = useState<WebhookEndpoint[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);

  const toggleEvent = (event: string) => {
    setSelectedEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
    );
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Webhooks</h2>
          <p className="text-sm text-muted-foreground">Send real-time event data to external services</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          Add endpoint
        </button>
      </div>

      {showCreate && (
        <div className="rounded-md border border-border p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground">Endpoint URL</label>
            <input type="url" required value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://your-server.com/webhook"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Events to subscribe</label>
            <div className="grid grid-cols-2 gap-1">
              {AVAILABLE_EVENTS.map((event) => (
                <label key={event} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={selectedEvents.includes(event)}
                    onChange={() => toggleEvent(event)} />
                  <span className="text-muted-foreground font-mono text-xs">{event}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-md bg-muted p-3">
            <p className="text-xs text-muted-foreground">
              A signing secret will be auto-generated. All webhook deliveries include
              X-Ease-Signature (HMAC-SHA256), X-Ease-Timestamp, and X-Ease-Delivery-Id headers.
            </p>
          </div>
          <div className="flex gap-3">
            <button disabled={!newUrl || selectedEvents.length === 0}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              Create endpoint
            </button>
            <button onClick={() => { setShowCreate(false); setNewUrl(""); setSelectedEvents([]); }}
              className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent">
              Cancel
            </button>
          </div>
        </div>
      )}

      {endpoints.length === 0 ? (
        <div className="rounded-md border border-border p-8 text-center">
          <span className="text-4xl">🔗</span>
          <p className="mt-3 font-medium text-foreground">No webhook endpoints</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add an endpoint to receive event notifications in real-time.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {endpoints.map((ep) => (
            <div key={ep.id} className="rounded-md border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${
                    ep.status === "active" ? "bg-[var(--color-success)]/50" :
                    ep.status === "failing" ? "bg-[var(--color-destructive)]/50" : "bg-[var(--color-muted-foreground)]"
                  }`} />
                  <p className="text-sm font-mono text-foreground truncate max-w-xs">{ep.url}</p>
                </div>
                <span className="text-xs text-muted-foreground">{ep.successRate}% success (24h)</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {ep.events.map((event) => (
                  <span key={event} className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground font-mono">
                    {event}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
